import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import KanbanBoard from './components/KanbanBoard';
import Dashboard from './components/Dashboard';
import GlobalDashboard from './components/GlobalDashboard';
import BoardManager from './components/BoardManager';
import './App.css';

function App() {
  const [boards, setBoards] = useState({});
  const [currentBoardId, setCurrentBoardId] = useState('default');

  // Initialize with default board
  useEffect(() => {
    const savedBoards = localStorage.getItem('kanban-boards');
    if (savedBoards) {
      try {
        const parsedBoards = JSON.parse(savedBoards);
        setBoards(parsedBoards);
        // Set current board to the first available board
        const boardIds = Object.keys(parsedBoards);
        if (boardIds.length > 0) {
          setCurrentBoardId(boardIds[0]);
        }
      } catch (error) {
        console.error('Error loading boards from localStorage:', error);
        initializeDefaultBoard();
      }
    } else {
      initializeDefaultBoard();
    }
  }, []);

  const initializeDefaultBoard = () => {
    const defaultBoard = {
      id: 'default',
      name: 'Default Board',
      createdAt: new Date().toISOString(),
      columns: {
        'backlog': {
          id: 'backlog',
          title: 'Backlog',
          color: 'bg-gray-500',
          tasks: []
        },
        'todo': {
          id: 'todo',
          title: 'To Do',
          color: 'bg-red-500',
          tasks: []
        },
        'in-progress': {
          id: 'in-progress',
          title: 'In Progress',
          color: 'bg-yellow-500',
          tasks: []
        },
        'done': {
          id: 'done',
          title: 'Done',
          color: 'bg-green-500',
          tasks: []
        }
      }
    };
    setBoards({ default: defaultBoard });
  };

  const saveBoards = (updatedBoards) => {
    setBoards(updatedBoards);
    localStorage.setItem('kanban-boards', JSON.stringify(updatedBoards));
  };

  const createBoard = (name) => {
    const newBoardId = `board-${Date.now()}`;
    const newBoard = {
      id: newBoardId,
      name: name,
      createdAt: new Date().toISOString(),
      columns: {
        'backlog': {
          id: 'backlog',
          title: 'Backlog',
          color: 'bg-gray-500',
          tasks: []
        },
        'todo': {
          id: 'todo',
          title: 'To Do',
          color: 'bg-red-500',
          tasks: []
        },
        'in-progress': {
          id: 'in-progress',
          title: 'In Progress',
          color: 'bg-yellow-500',
          tasks: []
        },
        'done': {
          id: 'done',
          title: 'Done',
          color: 'bg-green-500',
          tasks: []
        }
      }
    };
    const updatedBoards = { ...boards, [newBoardId]: newBoard };
    saveBoards(updatedBoards);
    setCurrentBoardId(newBoardId);
    return newBoardId;
  };

  const deleteBoard = (boardId) => {
    if (Object.keys(boards).length <= 1) return false; // Can't delete last board
    
    const updatedBoards = { ...boards };
    delete updatedBoards[boardId];
    saveBoards(updatedBoards);
    
    // Switch to another board if current board was deleted
    if (currentBoardId === boardId) {
      const remainingBoards = Object.keys(updatedBoards);
      if (remainingBoards.length > 0) {
        setCurrentBoardId(remainingBoards[0]);
      }
    }
    return true;
  };

  const updateBoard = (boardId, updatedBoard) => {
    const updatedBoards = { ...boards, [boardId]: updatedBoard };
    saveBoards(updatedBoards);
  };

  const updateBoardName = (boardId, newName) => {
    if (boards[boardId] && boards[boardId].name !== newName) {
      const updatedBoards = {
        ...boards,
        [boardId]: {
          ...boards[boardId],
          name: newName
        }
      };
      saveBoards(updatedBoards);
    }
  };

  const switchBoard = (boardId) => {
    setCurrentBoardId(boardId);
  };

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <Routes>
          <Route 
            path="/" 
            element={
              <KanbanBoard 
                board={boards[currentBoardId]}
                boards={boards}
                currentBoardId={currentBoardId}
                onBoardSwitch={switchBoard}
                onCreateBoard={createBoard}
                onDeleteBoard={deleteBoard}
                onUpdateBoard={updateBoard}
              />
            } 
          />
          <Route 
            path="/dashboard/:boardId" 
            element={
              <Dashboard 
                boards={boards}
                currentBoardId={currentBoardId}
                onBoardSwitch={switchBoard}
              />
            } 
          />
          <Route 
            path="/global-dashboard" 
            element={
              <GlobalDashboard 
                boards={boards}
                onBoardSwitch={switchBoard}
              />
            } 
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;